import React from 'react';
import PropTypes from 'prop-types';

const AreaFilter = (props) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const filterRef = React.useRef();
    const togglerRef = React.useRef();
    const optionsRef = React.useRef();
    const focusIndexRef = React.useRef(null);
    // Selected areas are shown as pills and removed from the dropdown
    const unselectedAreas = props.areas.filter((area) => !props.selectedAreas.includes(area));

    const onToggle = (area) => {
        props.onChange(
            props.selectedAreas.includes(area)
                ? props.selectedAreas.filter((selectedArea) => selectedArea !== area)
                : [...props.selectedAreas, area]
        );
    };

    const onSelectOption = (area, index) => {
        focusIndexRef.current = index;
        onToggle(area);
    };

    // Selected option disappears from the list, so keep focus in the dropdown
    React.useEffect(() => {
        if (focusIndexRef.current === null) {
            return;
        }
        const options = optionsRef.current.querySelectorAll('.select-multiple__option');
        const nextOption = options[Math.min(focusIndexRef.current, options.length - 1)];
        (nextOption || togglerRef.current).focus();
        focusIndexRef.current = null;
    }, [props.selectedAreas]);

    const close = () => {
        setIsOpen(false);
        togglerRef.current.focus();
    };

    // Close dropdown on click outside
    React.useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onClickOutside = (e) => {
            if (!filterRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);

        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [isOpen]);

    return (
        <div
            className="find-installer-filter"
            id={props.id}
            ref={filterRef}
            onKeyDown={(e) => {
                if (isOpen && (e.key === 'Escape' || e.key === 'Esc')) {
                    close();
                }
            }}
        >
            {props.title &&
                <div className="find-installer-filter__label" id={`${props.id}-label`}>{props.title}</div>
            }
            <div className="select-multiple">
                <div className="select-multiple__wrapper">
                    {props.selectedAreas.map((area) => (
                        <button
                            type="button"
                            className="select-multiple__pill"
                            key={area}
                            aria-label={`${props.removeLabel} ${area}`}
                            onClick={() => onToggle(area)}
                        >
                            {area}
                            {/* Inline SVG keeps the icon crisp at sub-pixel positions, unlike a background image */}
                            <svg className="select-multiple__pill-icon" viewBox="0 0 8 8" aria-hidden="true" focusable="false">
                                <path d="M8 6.64 5.36 4 8 1.36 6.64 0 4 2.64 1.36 0 0 1.36 2.64 4 0 6.64 1.36 8 4 5.36 6.64 8z" />
                            </svg>
                        </button>
                    ))}
                    <button
                        type="button"
                        className="select-multiple__toggler"
                        ref={togglerRef}
                        aria-expanded={isOpen}
                        aria-controls={`${props.id}-options`}
                        aria-labelledby={props.title ? `${props.id}-label` : undefined}
                        onClick={() => setIsOpen((open) => !open)}
                    >
                        {props.placeholder}
                    </button>
                </div>
                <ul
                    className={`select-multiple__options ${isOpen ? 'select-multiple__options--open' : ''}`}
                    id={`${props.id}-options`}
                    ref={optionsRef}
                    hidden={!isOpen || !unselectedAreas.length}
                >
                    {unselectedAreas.map((area, index) => (
                        <li key={area}>
                            <button
                                type="button"
                                className="select-multiple__option"
                                onClick={() => onSelectOption(area, index)}
                            >
                                {area}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

AreaFilter.propTypes = {
    id: PropTypes.string,
    title: PropTypes.string,
    placeholder: PropTypes.string,
    removeLabel: PropTypes.string,
    areas: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedAreas: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired
};

AreaFilter.defaultProps = {
    id: 'find-installer-filter',
    placeholder: 'Filter',
    removeLabel: 'Remove'
};

export default AreaFilter;
